// src/repositories/notification.repository.ts
import { safeRequest, AppException } from '../api/safeRequest.ts';
import { toNumber, normalizePagination } from '../utils/toNumber.ts';
import { NotificationNetwork } from '../api/notification.network.ts';
import { useNotificationStore } from '../stores/notification.store.ts';
import type { NotificationItem } from '../models/notification.model.ts';

const PER_PAGE = 20;

function normalizeItem(n: any): NotificationItem {
  return {
    id: toNumber(n?.id, 0),
    target_uuid: String(n?.target_uuid ?? ''),
    target_type: n?.target_type ?? 'COMMUNITY',
    title: String(n?.title ?? ''),
    content: String(n?.content ?? ''),
    image: n?.image ?? null,
    is_read: Boolean(n?.is_read),
    created_at: String(n?.created_at ?? ''),
  };
}

export const NotificationRepository = {
  fetchList: async ({ page = 1 }: { page?: number } = {}) => {
    const store = useNotificationStore.getState();
    if (page === 1) store.setLoading(true);
    else store.setNextLoading(true);
    store.setError(null);

    try {
      const res = await safeRequest(
        () => NotificationNetwork.getList(page, PER_PAGE),
        { handleGlobally: false }
      );
      const data = (res as any)?.data ?? res;
      const notifications = (data?.notifications ?? []).map(normalizeItem);
      const pagination = normalizePagination(data?.pagination);

      if (page === 1) store.setList(notifications, pagination);
      else store.appendList(notifications, pagination);

      const hasUnread = notifications.some((n: NotificationItem) => !n.is_read);
      if (page === 1 && hasUnread) store.setHasUnread(false);

      return { notifications, pagination };
    } catch (e: any) {
      const msg =
        e instanceof AppException
          ? e.message
          : e?.response?.data?.message || e?.message || 'Failed to load notifications.';
      store.setError(msg);
      return null;
    } finally {
      store.setLoading(false);
      store.setNextLoading(false);
    }
  },

  markRead: async (id?: number) => {
    const store = useNotificationStore.getState();
    if (id != null) store.markItemRead(id);
    else store.markAllRead();

    try {
      await safeRequest(() => NotificationNetwork.markRead(id), { handleGlobally: false });
      if (id == null) store.setHasUnread(false);
      return true;
    } catch {
      return false;
    }
  },

  remove: async (id?: number) => {
    const store = useNotificationStore.getState();
    if (id != null) store.removeItem(id);

    try {
      await safeRequest(() => NotificationNetwork.remove(id), { handleGlobally: false });
      if (id == null) {
        const remaining = store.notifications.filter((n) => !n.is_read);
        store.setList(remaining, store.pagination ?? { total_items: remaining.length, current_page: 1, total_page: 1 });
      }
      return true;
    } catch {
      return false;
    }
  },

  checkNewNotifications: async () => {
    try {
      const res = await safeRequest(() => NotificationNetwork.checkNew(), { handleGlobally: false });
      const data = (res as any)?.data ?? res;
      const hasUnread = Boolean(data?.new_notice);
      useNotificationStore.getState().setHasUnread(hasUnread);
      return hasUnread;
    } catch {
      return false;
    }
  },
};
